import Vue from 'vue';
import throttle from 'lodash/throttle';
import {
  FormDefinition, VesselGlobal, VesselConsumptionConfig, SharedGlobal, ExistingSubmission,
} from '../forms/types/FormDefinition';
import { SubmissionGlobalValue } from '../formulas/types/ComputerDefinition';
import EventBus from '../EventsBus';
import { ValidationError } from './types/Validation';
import Validate from './utils/validator';
import { getFieldGlobalId } from './utils/getFieldGlobalId';
import { getPreviousGlobalValue } from '../formulas/utils/helpers';
import {
  FieldDefinition, FieldDependency, FieldType, GlobalValue,
} from './types/FieldDefinition';

export interface FieldContext {
  computer?: any,
  formDef: FormDefinition,
  submissionGlobalValues: SubmissionGlobalValue[],
  sharedGlobals: SharedGlobal[],
  vesselGlobals: VesselGlobal[],
  vesselConsumptionConfig: VesselConsumptionConfig[],
  initialisedFields: Field[],
  updatedGlobals: SubmissionGlobalValue[],
  updateGlobal(value: Omit<GlobalValue, 'id' | 'reportDate'>): void,
  createField(feildDef: FieldDefinition): Field,
  deleteField(field: Field): void,
  existingSubmission?: ExistingSubmission
}

/**
 * The Field class handles the updating and computation of itself from referenced
 * fields linked to it from formulas.
 */
export class Field {
  public id: string;
  public fieldDef: FieldDefinition;
  public formDef: FormDefinition;
  public state: any;
  public label: string;
  public type: FieldType;
  public component?: any;
  public context: FieldContext;

  constructor({
    fieldDef, context,
  }: {
    fieldDef: FieldDefinition,
    context: FieldContext
  }) {
    // Set primary data.
    this.id = fieldDef.id;
    this.formDef = context.formDef;
    this.fieldDef = fieldDef;
    this.context = context;
    this.label = this.fieldDef.title || '';
    this.type = this.fieldDef.type;
    this.component = null;

    // Convert single formulas to an array for newer fields.
    this.fieldDef.formulas = (this.fieldDef.formula) ? [this.fieldDef.formula] : [];

    // Gather field dependencies based on its formulas.
    let dependencies: FieldDependency[] = [];
    if (this.context.computer) {
      dependencies = this.context.computer.getDependencies(this.fieldDef.formulas);
    }

    // Add dependencies from validation formulas too
    if (this.fieldDef.validation?.some((instance) => instance.formula)) {
      const instanceFormualas: string[] = this.fieldDef.validation
        .filter((instance) => instance.formula)
        .map(({ formula }) => formula!);

      const formulaDependecies = this.context.computer.getDependencies(instanceFormualas);

      dependencies = [...formulaDependecies, ...dependencies];
    }

    // Create a reactive state for the field.
    this.state = Vue.observable({
      content: this.fieldDef.default || '',
      disabled: this.fieldDef.disabled || false,
      visible: true, // Based on computed values
      edited: false, // When to show validation errors
      errors: [],
      dependencies,
      equals: false,
      placeholder: this.fieldDef.placeholder || '',
      editable: true,
      validationEnabled: true,
    });

    /**
     * Create event listeners for each one of the field's dependencies,
     * but throttle by 200ms to curb circular dependency crashes.
     */
    this.state.dependencies.forEach((dependency) => {
      const eventListenerString = `${dependency.id}:updated${(dependency.type === 'global' ? ':global' : '')}`;
      EventBus.$on(eventListenerString, throttle(() => { this.handleDependencyUpdated(); }, 200));
    });
  }

  /**
   * 1. Attempt to find a related global value and use this.
   * 2. Use a default value.
   * 3. Set initial value to an empty string.
   */
  initialiseValue(reportDate: Date | string, globalValues: SubmissionGlobalValue[]): any {
    const { fieldDef } = this;

    let content: any = '';

    if (Object.prototype.hasOwnProperty.call(fieldDef, 'default') && fieldDef.default !== '') {
      this.state.content = fieldDef.default;
      content = fieldDef.default;
    }

    if (fieldDef?.global?.setAsDefault) {
      const previousValue = getPreviousGlobalValue({
        globalId: fieldDef.global.id,
        globalValues,
        reportDate,
        vesselGlobals: this.context.vesselGlobals,
      });

      if (previousValue) {
        content = previousValue.value;
      }
    }

    this.state.content = content;
    this.state.edited = false;

    // Run preliminary validation for our initialised field.
    this.validate();
  }

  /**
   * Get Dependencies
   */
  getDependencies(): void {
    this.state.dependencies = this.context.computer.getDependencies(this.fieldDef.formulas);
  }

  /**
   * Set this fields content, and emit a global content update if possble.
   * @param value
   */
  setContent(value: any): void {
    // If the new value is the same as the old value just return early.
    // This can help to prevent infinite loops
    if (this.state.content === value) {
      this.validate();
      return;
    }

    this.state.content = Number(value) === -Infinity ? 0 : value;
    EventBus.$emit(`${this.fieldDef.id}:updated`, this.state.content);

    if (this.fieldDef.global || this.fieldDef.compositeGlobal) {
      this.context.updateGlobal({
        globalId: getFieldGlobalId(this.fieldDef)!,
        value: this.state.content,
      });
    }

    this.validate();
  }

  /**
   * Set the fields content from an existing value.
   * This generally happens when initialised, but this won't emit updated events.
   * This will also process array values (repeatable fields).
   */
  setExistingContent(value: any): void {
    if (Array.isArray(value) && value.length) {
      // If the value is an array it means its part of a repeatable field
      // in this situation we should do nothing because the repeatable field
      // will handle initialising its content
      return;
    }

    this.state.content = value;

    if (this.fieldDef.global || this.fieldDef.compositeGlobal) {
      this.context.updateGlobal({
        globalId: getFieldGlobalId(this.fieldDef)!,
        value: this.state.content,
      });
    }
  }

  /**
   * Get this fields content
   */
  getContent(): any {
    return this.state.content;
  }

  /**
   * Return the id of the field
   */
  getId(): string {
    return this.fieldDef.id;
  }

  /**
   * A local value that reflects if the entire form is editable.
   */
  setEditable(canEdit: boolean): void {
    this.state.editable = canEdit;
  }

  /**
   * Validate the field
   */
  validate(): void {
    if (this.state.visible && this.state.validationEnabled && this.fieldDef.validation?.length) {
      this.state.errors = Validate({
        content: this.state.content,
        computer: this.context.computer,
        validation: this.fieldDef.validation,
      });
    } else {
      this.state.errors = [];
    }
  }

  /**
   * Every time a field in a formula is updated.
   */
  handleDependencyUpdated(): void {
    const computedValues = this.context.computer.compute(this.fieldDef.formulas);


    computedValues.forEach((item) => {
      if (item === null) return;
      this[item.callback](item.value);

      this.validate();
    });
  }

  /**
   * This will be called for fields with an `equals` formula.
   */
  setEquals(value: boolean): void {
    this.state.equals = value;
  }

  /**
   * Get current field errors.
   */
  getErrors(): ValidationError[] {
    return this.state.errors;
  }

  /**
   * Set if a field should be disabled.
   */
  setDisabled(value: boolean): void {
    this.state.disabled = value;
  }

  /**
   * Get if the user should be able to edit this field.
   * (`true` would mean the field IS disabled)
   */
  getDisabled(): boolean {
    return (this.state.disabled || !this.state.editable);
  }

  /**
   * Set if a field is visible.
   */
  setVisibility(value: boolean): void {
    this.state.visible = value;

    // Also needs to do the same for all of its children
    // eslint-disable-next-line no-unused-expressions
    this.fieldDef.children?.forEach((childId) => {
      const field = this.context.initialisedFields.find((f) => f.id === childId);
      if (field) {
        field.setVisibility(value);
      }
    });
  }

  /**
   * Set edited state.
   */
  setEdited(value: boolean): void {
    this.state.edited = value;
  }

  /**
   * Enable field validation
   */
  enableValidation(): void {
    this.state.validationEnabled = true;
  }

  /**
   * Enable field validation
   */
  disableValidation(): void {
    this.state.validationEnabled = false;
    this.state.errors = [];
  }

  /**
   * Set the component that this field is linked to.
   */
  setComponent(value: any): void {
    this.component = value;
  }

  /**
   * Return the component this field is linked to.
   */
  getComponent(): any {
    return this.component;
  }

  /**
   * Return the Ids of this field's children.
   */
  getChildIds(): string[] {
    return (this.fieldDef?.children?.length)
      ? this.fieldDef.children
      : [];
  }

  /**
   * This is run when a field is removed and
   * will destroy all the reference event listeners.
   */
  destroy() {
    this.state.dependencies.forEach((dependency) => {
      EventBus.$off(`${dependency}:updated`);
    });
  }
}
